'use client';
import React, { useState } from 'react';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import AdminFirstLoginForm from './AdminFirstLoginForm';
import AppLogo from '@/components/ui/AppLogo';
import { GraduationCap, Heart, Shield, Award } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const programs = [
  { name: 'Home Health Aid', abbr: 'HHA', color: 'bg-teal-100 text-teal-700' },
  { name: 'Nursing Assistant', abbr: 'CNA', color: 'bg-blue-100 text-blue-700' },
  { name: 'Medication Administration', abbr: 'Med Admin', color: 'bg-purple-100 text-purple-700' },
  { name: 'CPR & First Aid', abbr: 'CPR', color: 'bg-red-100 text-red-700' },
  { name: 'Medical Assistant', abbr: 'MA', color: 'bg-orange-100 text-orange-700' },
  { name: 'Medical Billing & Coding', abbr: 'MBC', color: 'bg-indigo-100 text-indigo-700' },
  { name: 'Massage Therapy', abbr: 'MT', color: 'bg-pink-100 text-pink-700' },
];

export default function AuthContainer() {
  const [activeTab, setActiveTab] = useState<'login' | 'register' | 'admin'>('login');

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-[45%] xl:w-[42%] bg-gradient-to-br from-teal-800 via-teal-700 to-teal-900 flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-teal-600/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-teal-900/40 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-teal-600/10 rounded-full blur-3xl" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={44} />
            <div>
              <span className="font-display font-800 text-white text-2xl block leading-tight">STARGA LMS</span>
              <span className="text-teal-200 text-xs font-medium tracking-wide">Vocational Learning Platform</span>
            </div>
          </div>

          <div className="mb-10">
            <h1 className="font-display font-800 text-white text-3xl xl:text-4xl leading-tight mb-4">
              Launch Your<br />
              <span className="text-teal-200">Healthcare Career</span><br />
              Today
            </h1>
            <p className="text-teal-100 text-base leading-relaxed max-w-sm">
              Access world-class vocational training from anywhere. Earn industry-recognized certifications in healthcare and wellness.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-10">
            {[
              { value: '1,240+', label: 'Graduates' },
              { value: '7', label: 'Programs' },
              { value: '98%', label: 'Pass Rate' },
            ]?.map(stat => (
              <div key={`stat-${stat?.label}`} className="bg-white/10 backdrop-blur-sm rounded-xl p-3 text-center border border-white/10">
                <div className="font-display font-700 text-white text-xl tabular-nums">{stat?.value}</div>
                <div className="text-teal-200 text-xs mt-0.5">{stat?.label}</div>
              </div>
            ))}
          </div>

          {/* Programs offered */}
          <div>
            <p className="text-teal-300 text-xs font-semibold uppercase tracking-widest mb-3">Programs Offered</p>
            <div className="flex flex-wrap gap-2">
              {programs?.map(prog => (
                <span key={`prog-${prog?.abbr}`} className="px-2.5 py-1 bg-white/10 text-white text-xs font-medium rounded-lg border border-white/10 backdrop-blur-sm">
                  {prog?.abbr}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom features */}
        <div className="relative z-10 grid grid-cols-2 gap-3">
          {[
            { icon: GraduationCap, text: 'Self-paced learning' },
            { icon: Heart, text: 'Clinical skills labs' },
            { icon: Shield, text: 'State-approved programs' },
            { icon: Award, text: 'Certification prep' },
          ]?.map(feat => {
            const Icon = feat?.icon;
            return (
              <div key={`feat-${feat?.text}`} className="flex items-center gap-2.5 bg-white/5 rounded-lg p-2.5 border border-white/10">
                <div className="w-7 h-7 bg-teal-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon size={14} className="text-teal-200" />
                </div>
                <span className="text-teal-100 text-xs font-medium">{feat?.text}</span>
              </div>
            );
          })}
        </div>
      </div>
      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-10 overflow-y-auto">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={36} />
          <span className="font-display font-700 text-teal-700 text-xl">STARGA LMS</span>
        </div>

        <div className="w-full max-w-md">
          {/* Tabs */}
          <div className="flex bg-gray-100 rounded-xl p-1 mb-8">
            <button
              onClick={() => setActiveTab('login')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === 'login' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setActiveTab('register')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === 'register' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Student Apply
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={`flex-1 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${activeTab === 'admin' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Admin
            </button>
          </div>

          {activeTab === 'login' ? (
            <LoginForm onSwitchToRegister={() => setActiveTab('register')} />
          ) : activeTab === 'register' ? (
            <RegisterForm onSwitchToLogin={() => setActiveTab('login')} programs={programs} />
          ) : (
            <AdminFirstLoginForm onSwitchToLogin={() => setActiveTab('login')} />
          )}
        </div>
      </div>
    </div>
  );
}